import { useState, useEffect, useMemo, useRef } from 'react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserPlus, FaSort, FaSortUp, FaSortDown, FaSearch, FaTimes, FaGlobe, FaUserEdit, FaCog, FaUpload, FaPlus, FaChevronDown, FaTrash, FaPhoneAlt, FaStar, FaArchive, FaInbox } from 'react-icons/fa';
import ImportApplicantsModal from '../../recruiting/components/ImportApplicantsModal';
import { toast } from 'react-toastify';
import { personalakteStyles as ps, colors, shadows } from '../styles/personalakteStyles';
import PillFilterDropdown from '../../../components/PillFilterDropdown';
import RecruitingService from '../../recruiting/services/RecruitingService';
import StatusBadge from '../../recruiting/components/StatusBadge';
import InlineInterviewPanel from '../../recruiting/components/InlineInterviewPanel';
import type { Applicant, ApplicantStatus, ApplicantFilters, ApplicantTriage, TeamChoice, DesiredEmploymentType } from '../../recruiting/types';

type EmployeesTabKey = 'employees' | 'payrolls' | 'documents' | 'recruiting';

const TABS: { key: EmployeesTabKey; label: string; to: string }[] = [
  { key: 'employees', label: 'Employees', to: '/employees' },
  { key: 'payrolls', label: 'Payrolls', to: '/employees/payrolls' },
  { key: 'documents', label: 'Documents', to: '/employees/documents' },
  { key: 'recruiting', label: 'Recruiting', to: '/employees/recruiting' },
];

const STATUS_PILLS: { value: ApplicantStatus; label: string; bg: string; color: string }[] = [
  { value: 'NEW', label: 'Neu', bg: '#dbeafe', color: '#1e40af' },
  { value: 'CONFIRMATION_SENT', label: 'Bestätigt', bg: '#e0e7ff', color: '#3730a3' },
  { value: 'PHONE_INTERVIEW_SCHEDULED', label: 'Interview geplant', bg: '#fef3c7', color: '#92400e' },
  { value: 'PHONE_INTERVIEW_DONE', label: 'Interview done', bg: '#fde68a', color: '#78350f' },
  { value: 'TRIAL_SCHEDULED', label: 'Probe geplant', bg: '#fbcfe8', color: '#9d174d' },
  { value: 'TRIAL_DONE', label: 'Probe done', bg: '#f9a8d4', color: '#831843' },
  { value: 'HIRED', label: 'Eingestellt', bg: '#dcfce7', color: '#166534' },
  { value: 'REJECTED', label: 'Abgelehnt', bg: '#fee2e2', color: '#991b1b' },
];

const TYPE_PILLS: { value: DesiredEmploymentType; label: string; color: string }[] = [
  { value: 'MINIJOB', label: 'Minijob', color: '#6366f1' },
  { value: 'WERKSTUDENT', label: 'Werkstudent', color: '#8b5cf6' },
  { value: 'TEILZEIT', label: 'Teilzeit', color: '#0ea5e9' },
  { value: 'VOLLZEIT', label: 'Vollzeit', color: '#059669' },
  { value: 'KURZFRISTIG', label: 'Kurzfristig', color: '#d97706' },
];

const TEAM_PILLS: { value: TeamChoice; label: string; color: string }[] = [
  { value: 'SERVICE', label: 'Service', color: '#0ea5e9' },
  { value: 'PRODUCTION', label: 'Produktion', color: '#f39c12' },
  { value: 'MARKETING', label: 'Marketing', color: '#3b82f6' },
];

const TRIAGE_TABS: { key: ApplicantTriage; label: string }[] = [
  { key: 'INBOX', label: 'Eingang' },
  { key: 'SHORTLIST', label: 'Shortlist' },
  { key: 'ARCHIVED', label: 'Archiv' },
];

// Applicants without a stored triage (created before the feature) count as Eingang.
const bucketOf = (a: Applicant): ApplicantTriage => a.triage || 'INBOX';

type SortKey = 'name' | 'email' | 'status' | 'team' | 'employment' | 'date';

function formatDate(iso?: string): string {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}


export default function Recruiting(): React.JSX.Element {
  const navigate = useNavigate();
  const activeTab: EmployeesTabKey = 'recruiting';

  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeBucket, setActiveBucket] = useState<ApplicantTriage>('INBOX');

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilters, setStatusFilters] = useState<Set<ApplicantStatus>>(new Set());
  const [teamFilters, setTeamFilters] = useState<Set<TeamChoice>>(new Set());
  const [employmentFilters, setEmploymentFilters] = useState<Set<DesiredEmploymentType>>(new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [sortBy, setSortBy] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showStatusSubmenu, setShowStatusSubmenu] = useState(false);

  const actionsMenuRef = useRef<HTMLDivElement>(null);

  const [expandedInterviewId, setExpandedInterviewId] = useState<number | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [newApplicant, setNewApplicant] = useState({ first_name: '', last_name: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    loadApplicants();
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeBucket, statusFilters, teamFilters, employmentFilters, searchTerm, dateFrom, dateTo]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target as Node)) {
        setShowActionsMenu(false);
        setShowStatusSubmenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadApplicants = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: ApplicantFilters = {};
      const data = await RecruitingService.fetchApplicants(filters);
      setApplicants(data);
    } catch (e: any) {
      setError('Bewerber konnten nicht geladen werden.');
      console.error('[Recruiting] load error:', e);
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const renderSortIcon = (key: SortKey): React.ReactNode => {
    const isActive = sortBy === key;
    const color = isActive ? colors.navy : colors.textMuted;
    const style = { marginLeft: 6, verticalAlign: 'middle' } as React.CSSProperties;
    if (!isActive) return <FaSort style={style} color={color} />;
    return sortDir === 'asc' ? <FaSortUp style={style} color={color} /> : <FaSortDown style={style} color={color} />;
  };

  const bucketCounts = useMemo(() => {
    const counts: Record<ApplicantTriage, number> = { INBOX: 0, SHORTLIST: 0, ARCHIVED: 0 };
    applicants.forEach(a => { counts[bucketOf(a)] += 1; });
    return counts;
  }, [applicants]);

  const filteredAndSorted = useMemo(() => {
    let result = applicants.filter(a => bucketOf(a) === activeBucket);

    if (statusFilters.size > 0) result = result.filter(a => statusFilters.has(a.status));
    if (teamFilters.size > 0) result = result.filter(a => a.team_choice != null && teamFilters.has(a.team_choice));
    if (employmentFilters.size > 0) result = result.filter(a => a.desired_employment_type != null && employmentFilters.has(a.desired_employment_type));

    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      result = result.filter(a => a.applied_at && new Date(a.applied_at) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter(a => a.applied_at && new Date(a.applied_at) <= to);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(a =>
        `${a.first_name} ${a.last_name}`.toLowerCase().includes(term) ||
        (a.email || '').toLowerCase().includes(term) ||
        (a.phone || '').includes(term)
      );
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (`${a.last_name} ${a.first_name}`).localeCompare(`${b.last_name} ${b.first_name}`) * dir;
        case 'email':
          return (a.email || '').localeCompare(b.email || '') * dir;
        case 'status':
          return a.status.localeCompare(b.status) * dir;
        case 'team':
          return (a.team_choice || '').localeCompare(b.team_choice || '') * dir;
        case 'employment':
          return (a.desired_employment_type || '').localeCompare(b.desired_employment_type || '') * dir;
        case 'date':
        default:
          return (a.applied_at || '').localeCompare(b.applied_at || '') * dir;
      }
    });

    return result;
  }, [applicants, activeBucket, statusFilters, teamFilters, employmentFilters, dateFrom, dateTo, searchTerm, sortBy, sortDir]);

  const handleAddApplicant = async () => {
    if (!newApplicant.first_name || !newApplicant.last_name || !newApplicant.email) {
      toast.error('Please fill in first name, last name and email.');
      return;
    }
    try {
      setSaving(true);
      await RecruitingService.createApplicant(newApplicant);
      toast.success('Candidate created.');
      setShowAddModal(false);
      setNewApplicant({ first_name: '', last_name: '', email: '' });
      loadApplicants();
    } catch (e: any) {
      toast.error('Failed to create candidate.');
      console.error('[Recruiting] create error:', e);
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectId = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (ids: number[]) => {
    const allSelected = ids.length > 0 && ids.every(id => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(ids));
  };

  const handleRowClick = (applicant: Applicant) => {
    if (applicant.status === 'PHONE_INTERVIEW_SCHEDULED') {
      setExpandedInterviewId(prev => prev === applicant.id ? null : applicant.id);
      return;
    }
    toggleSelectId(applicant.id);
  };

  const handleBulkStatusChange = async (newStatus: ApplicantStatus) => {
    setShowActionsMenu(false);
    setShowStatusSubmenu(false);
    const ids = Array.from(selectedIds);
    const statusLabel = STATUS_PILLS.find(s => s.value === newStatus)?.label || newStatus;

    const results = await Promise.allSettled(
      ids.map(id => RecruitingService.changeStatus(id, newStatus))
    );

    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    if (failed === 0) {
      toast.success(`${succeeded} Bewerber auf "${statusLabel}" gesetzt.`);
    } else {
      toast.warning(`${succeeded} erfolgreich, ${failed} fehlgeschlagen.`);
    }

    setSelectedIds(new Set());
    loadApplicants();
  };

  const handleBulkTriage = async (triage: ApplicantTriage) => {
    setShowActionsMenu(false);
    const ids = Array.from(selectedIds);
    const label = TRIAGE_TABS.find(t => t.key === triage)?.label || triage;

    const results = await Promise.allSettled(
      ids.map(id => RecruitingService.setTriage(id, triage))
    );

    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    if (failed === 0) {
      toast.success(`${succeeded} Bewerber nach "${label}" verschoben.`);
    } else {
      toast.warning(`${succeeded} verschoben, ${failed} fehlgeschlagen.`);
    }

    setSelectedIds(new Set());
    loadApplicants();
  };

  const handleBulkDelete = async () => {
    setShowActionsMenu(false);
    const ids = Array.from(selectedIds);
    const confirmed = window.confirm(`${ids.length} Bewerber unwiderruflich löschen?`);
    if (!confirmed) return;

    const results = await Promise.allSettled(
      ids.map(id => RecruitingService.deleteApplicant(id))
    );

    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    if (failed === 0) {
      toast.success(`${succeeded} Bewerber gelöscht.`);
    } else {
      toast.warning(`${succeeded} gelöscht, ${failed} fehlgeschlagen.`);
    }

    setSelectedIds(new Set());
    loadApplicants();
  };

  const sourceIcon = (source: string) =>
    source === 'WEBSITE'
      ? <FaGlobe style={{ color: colors.info, fontSize: 11 }} title="Website" />
      : <FaUserEdit style={{ color: colors.textMuted, fontSize: 11 }} title="Manuell" />;

  const thStyle: React.CSSProperties = {
    padding: '8px 6px',
    textAlign: 'left',
    position: 'sticky',
    top: 0,
    background: colors.bgMuted,
    zIndex: 2,
    fontWeight: 600,
    fontSize: 11,
    color: colors.textMuted,
    letterSpacing: '0.2px',
    border: 'none',
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: '6px 6px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const dateInputStyle: React.CSSProperties = {
    padding: '5px 8px',
    borderRadius: 8,
    border: `1px solid ${colors.border}`,
    fontSize: 11,
    color: colors.textBody,
    background: colors.bgCard,
    outline: 'none',
    width: 120,
  };

  const hasSelection = selectedIds.size > 0;

  return (
    <div style={{ padding: '16px', position: 'relative', fontSize: '0.9rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div
        className="glass-header"
        style={{ padding: '12px 16px', marginBottom: '16px', position: 'relative', zIndex: 100 }}
      >
        {/* Row 1: Tabs + Add Button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => navigate(tab.to)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    border: isActive ? '1px solid rgba(82, 113, 165, 0.3)' : '1px solid transparent',
                    background: isActive ? 'rgba(82, 113, 165, 0.12)' : 'transparent',
                    color: isActive ? '#5271A5' : '#64748b',
                    borderRadius: '9999px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    transition: 'all 0.15s',
                    position: 'relative',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(82, 113, 165, 0.06)';
                      e.currentTarget.style.color = '#5271A5';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#64748b';
                    }
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setShowAddModal(true)}
            style={{
              ...ps.buttonPrimary,
              gap: '5px',
              padding: '8px 14px',
              fontSize: '11px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(82, 113, 165, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(231, 0, 125, 0.15)';
            }}
          >
            <FaPlus style={{ fontSize: '9px' }} /> Candidate
          </button>
        </div>

        {/* Row 2: Search + Filter Dropdowns + Date Range + Actions */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: '0 0 200px' }}>
            <FaSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: colors.textMuted, fontSize: 12 }} />
            <input
              type="text"
              placeholder="Name, E-Mail, Telefon..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px 6px 30px',
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
                fontSize: 12,
                color: colors.textBody,
                background: colors.bgCard,
                outline: 'none',
              }}
            />
            {searchTerm && (
              <FaTimes
                onClick={() => setSearchTerm('')}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: colors.textMuted, fontSize: 11 }}
              />
            )}
          </div>

          <PillFilterDropdown
            options={STATUS_PILLS}
            selected={statusFilters}
            onChange={setStatusFilters}
            label="Alle Status"
          />

          <PillFilterDropdown
            options={TYPE_PILLS}
            selected={employmentFilters}
            onChange={setEmploymentFilters}
            label="Alle Typen"
          />

          <PillFilterDropdown
            options={TEAM_PILLS}
            selected={teamFilters}
            onChange={setTeamFilters}
            label="Alle Teams"
          />

          {/* Date Range */}
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              style={dateInputStyle}
              title="Von"
            />
            <span style={{ fontSize: 11, color: colors.textMuted }}>–</span>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              style={dateInputStyle}
              title="Bis"
            />
            {(dateFrom || dateTo) && (
              <FaTimes
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                style={{ cursor: 'pointer', color: colors.textMuted, fontSize: 11 }}
                title="Datum zurücksetzen"
              />
            )}
          </div>

          {/* Right: Actions */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => navigate('/employees/recruiting/settings')}
              title="Einstellungen"
              aria-label="Einstellungen"
              style={{
                background: 'rgba(255, 255, 255, 0.9)',
                color: colors.textBody,
                border: `1px solid ${colors.border}`,
                borderRadius: '9999px',
                width: 34,
                height: 34,
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <FaCog size={14} />
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              title="Bewerber importieren"
              aria-label="Bewerber importieren"
              style={{
                background: 'rgba(255, 255, 255, 0.9)',
                color: colors.textBody,
                border: `1px solid ${colors.border}`,
                borderRadius: '9999px',
                width: 34,
                height: 34,
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <FaUpload size={14} />
            </button>

            {/* Actions (N) - visible when rows selected */}
            <div ref={actionsMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => {
                  if (!hasSelection) return;
                  setShowActionsMenu(prev => !prev);
                  setShowStatusSubmenu(false);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 14px',
                  borderRadius: 9999,
                  border: hasSelection ? `1px solid ${colors.magenta}40` : `1px solid ${colors.border}`,
                  background: hasSelection ? `${colors.magenta}10` : 'rgba(255, 255, 255, 0.9)',
                  color: hasSelection ? colors.magenta : colors.textMuted,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: hasSelection ? 'pointer' : 'default',
                  transition: 'all 0.15s',
                  opacity: hasSelection ? 1 : 0.5,
                }}
              >
                Actions ({selectedIds.size}) <FaChevronDown size={10} />
              </button>

              {showActionsMenu && hasSelection && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 4,
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 12,
                  boxShadow: shadows.elevated,
                  minWidth: 200,
                  zIndex: 1000,
                  overflow: 'hidden',
                }}>
                  <button
                    onClick={() => setShowStatusSubmenu(prev => !prev)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: 12,
                      color: colors.textBody,
                      textAlign: 'left',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    Status ändern <FaChevronDown size={9} style={{ transform: showStatusSubmenu ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                  </button>

                  {showStatusSubmenu && (
                    <div style={{
                      padding: '10px 14px 14px',
                      borderTop: `1px solid ${colors.borderLight}`,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                    }}>
                      {STATUS_PILLS.map(pill => (
                        <button
                          key={pill.value}
                          onClick={() => handleBulkStatusChange(pill.value)}
                          style={{
                            padding: '5px 14px',
                            borderRadius: 9999,
                            border: `1px solid ${pill.color}30`,
                            background: pill.bg,
                            color: pill.color,
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 500,
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = pill.color;
                            e.currentTarget.style.color = '#fff';
                            e.currentTarget.style.borderColor = pill.color;
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = pill.bg;
                            e.currentTarget.style.color = pill.color;
                            e.currentTarget.style.borderColor = `${pill.color}30`;
                          }}
                        >
                          {pill.label}
                        </button>
                      ))}
                    </div>
                  )}

                  <div style={{ height: 1, background: colors.borderLight }} />

                  {TRIAGE_TABS.filter(t => t.key !== activeBucket).map(t => {
                    const label = t.key === 'SHORTLIST' ? 'Auf Shortlist' : t.key === 'ARCHIVED' ? 'Archivieren' : 'Zurück in Eingang';
                    const icon = t.key === 'SHORTLIST' ? <FaStar size={11} /> : t.key === 'ARCHIVED' ? <FaArchive size={11} /> : <FaInbox size={11} />;
                    const iconColor = t.key === 'SHORTLIST' ? '#d97706' : t.key === 'ARCHIVED' ? colors.textMuted : colors.slate;
                    return (
                      <button
                        key={t.key}
                        onClick={() => handleBulkTriage(t.key)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          padding: '10px 14px',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          fontSize: 12,
                          color: colors.textBody,
                          textAlign: 'left',
                          fontWeight: 500,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span style={{ color: iconColor, display: 'inline-flex' }}>{icon}</span> {label}
                      </button>
                    );
                  })}

                  <div style={{ height: 1, background: colors.borderLight }} />

                  <button
                    onClick={handleBulkDelete}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '10px 14px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: 12,
                      color: colors.danger,
                      textAlign: 'left',
                      fontWeight: 500,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = colors.dangerLight; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <FaTrash size={11} /> Löschen
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Triage tabs: Eingang · Shortlist · Archiv */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {TRIAGE_TABS.map(tab => {
          const isActive = activeBucket === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveBucket(tab.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '7px 16px',
                borderRadius: 9999,
                border: isActive ? `1px solid ${colors.magenta}40` : `1px solid ${colors.border}`,
                background: isActive ? `${colors.magenta}12` : colors.bgCard,
                color: isActive ? colors.magenta : colors.textMuted,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = colors.bgHover; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = colors.bgCard; }}
            >
              {tab.label}
              <span style={{
                fontSize: 11,
                fontWeight: 700,
                minWidth: 18,
                textAlign: 'center',
                padding: '1px 6px',
                borderRadius: 9999,
                background: isActive ? colors.magenta : colors.bgMuted,
                color: isActive ? '#fff' : colors.textMuted,
              }}>
                {bucketCounts[tab.key]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: colors.textMuted, fontSize: 13 }}>
          Lade Bewerber...
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: 60, color: colors.danger, fontSize: 13 }}>
          {error}
          <br />
          <button
            onClick={loadApplicants}
            style={{ marginTop: 12, padding: '6px 16px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCard, cursor: 'pointer', fontSize: 12 }}
          >
            Erneut versuchen
          </button>
        </div>
      ) : applicants.length === 0 ? (
        <div
          className="glass-container"
          style={{ padding: 24, minHeight: 400 }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(82, 113, 165, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
              <FaUserPlus size={32} color="#5271A5" />
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 18, fontWeight: 600, color: colors.textBody }}>
              Noch keine Bewerber
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: colors.textMuted, maxWidth: 360, lineHeight: 1.5 }}>
              Bewerber erscheinen hier sobald sie sich über das öffentliche Formular bewerben oder manuell angelegt werden.
            </p>
          </div>
        </div>
      ) : (
        <div style={{
          background: colors.bgCard,
          borderRadius: 16,
          border: `1px solid ${colors.border}`,
          boxShadow: shadows.card,
          minHeight: 'calc(100vh - 220px)',
          maxHeight: 'calc(100vh - 220px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          position: 'relative',
          zIndex: 1,
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '34px' }} />
              <col style={{ width: '28px' }} />
              <col style={{ width: '170px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '90px' }} />
              <col style={{ width: '100px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '90px' }} />
            </colgroup>
            <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
              <tr style={{ borderBottom: 'none' }}>
                <th style={{ ...thStyle, textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    aria-label="Alle markieren"
                    checked={filteredAndSorted.length > 0 && filteredAndSorted.every(a => selectedIds.has(a.id))}
                    ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && !filteredAndSorted.every(a => selectedIds.has(a.id)); }}
                    onChange={() => toggleSelectAllVisible(filteredAndSorted.map(a => a.id))}
                    style={{ cursor: 'pointer', accentColor: colors.magenta }}
                  />
                </th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Src</th>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('name')}>Name {renderSortIcon('name')}</th>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('email')}>E-Mail {renderSortIcon('email')}</th>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('team')}>Team {renderSortIcon('team')}</th>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('employment')}>Typ {renderSortIcon('employment')}</th>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('date')}>Beworben {renderSortIcon('date')}</th>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status {renderSortIcon('status')}</th>
                <th style={thStyle}>Telefon</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.map(applicant => {
                const isSelected = selectedIds.has(applicant.id);
                const isExpanded = expandedInterviewId === applicant.id;
                const isInterviewReady = applicant.status === 'PHONE_INTERVIEW_SCHEDULED';
                return (
                  <React.Fragment key={applicant.id}>
                    <tr
                      style={{
                        borderBottom: isExpanded ? 'none' : `1px solid ${colors.borderLight}`,
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        background: isExpanded ? 'rgba(82, 113, 165, 0.06)' : isSelected ? 'rgba(231, 0, 125, 0.06)' : 'transparent',
                        borderLeft: isExpanded ? `3px solid ${colors.slate}` : isSelected ? `3px solid ${colors.magenta}` : '3px solid transparent',
                      }}
                      onMouseEnter={e => {
                        if (!isSelected && !isExpanded) e.currentTarget.style.background = colors.bgHover;
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = isExpanded ? 'rgba(82, 113, 165, 0.06)' : isSelected ? 'rgba(231, 0, 125, 0.06)' : 'transparent';
                      }}
                      onClick={() => handleRowClick(applicant)}
                    >
                      <td style={{ ...tdStyle, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`${applicant.first_name} ${applicant.last_name} markieren`}
                          checked={isSelected}
                          onChange={() => toggleSelectId(applicant.id)}
                          style={{ cursor: 'pointer', accentColor: colors.magenta }}
                        />
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{sourceIcon(applicant.source)}</td>
                      <td style={{ ...tdStyle, fontWeight: 500, color: colors.textPrimary }}>
                        <span
                          onClick={(e) => { e.stopPropagation(); navigate(`/employees/recruiting/${applicant.id}`); }}
                          style={{ cursor: 'pointer', textDecoration: 'none' }}
                          onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                          onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                        >
                          {applicant.first_name} {applicant.last_name}
                        </span>
                        {isInterviewReady && !isExpanded && (
                          <FaPhoneAlt style={{ marginLeft: 6, fontSize: 9, color: colors.slate, opacity: 0.6 }} title="Klicken für Interviewleitfaden" />
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: colors.textMuted }}>{applicant.email}</td>
                      <td style={tdStyle}>{applicant.team_choice === 'SERVICE' ? 'Service' : applicant.team_choice === 'PRODUCTION' ? 'Produktion' : applicant.team_choice === 'MARKETING' ? 'Marketing' : '\u2013'}</td>
                      <td style={{ ...tdStyle, fontSize: 11 }}>{applicant.desired_employment_type || '\u2013'}</td>
                      <td style={{ ...tdStyle, color: colors.textMuted }}>{formatDate(applicant.applied_at)}</td>
                      <td style={tdStyle}><StatusBadge status={applicant.status} /></td>
                      <td style={{ ...tdStyle, color: colors.textMuted, fontSize: 11 }}>{applicant.phone || '\u2013'}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0, borderBottom: `1px solid ${colors.borderLight}`, borderLeft: `3px solid ${colors.slate}` }}>
                          <InlineInterviewPanel
                            applicant={applicant}
                            onClose={() => setExpandedInterviewId(null)}
                            onSaved={() => { setExpandedInterviewId(null); loadApplicants(); }}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filteredAndSorted.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 40, textAlign: 'center', color: colors.textMuted, fontSize: 13 }}>
                    Keine Bewerber für die gewählten Filter gefunden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Applicant Modal */}
      {showAddModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowAddModal(false)}
        >
          <div
            style={{
              background: colors.bgCard, padding: 24, borderRadius: 16,
              width: '100%', maxWidth: 400, boxShadow: shadows.elevated,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: colors.navy, fontSize: 16, fontWeight: 700 }}>New Candidate</h3>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: colors.bgMuted, border: 'none', fontSize: 16,
                  cursor: 'pointer', color: colors.textMuted, padding: 0,
                  width: 28, height: 28, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', borderRadius: 8, transition: 'background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; }}
                onMouseLeave={e => { e.currentTarget.style.background = colors.bgMuted; }}
                title="Close"
              >
                ×
              </button>
            </div>
            <div style={{ ...ps.fieldWrapper, marginBottom: 12 }}>
              <label style={ps.labelStyle}>First Name</label>
              <input
                type="text"
                value={newApplicant.first_name}
                onChange={e => setNewApplicant(prev => ({ ...prev, first_name: e.target.value }))}
                style={ps.inputStyle}
              />
            </div>
            <div style={{ ...ps.fieldWrapper, marginBottom: 12 }}>
              <label style={ps.labelStyle}>Last Name</label>
              <input
                type="text"
                value={newApplicant.last_name}
                onChange={e => setNewApplicant(prev => ({ ...prev, last_name: e.target.value }))}
                style={ps.inputStyle}
              />
            </div>
            <div style={{ ...ps.fieldWrapper, marginBottom: 12 }}>
              <label style={ps.labelStyle}>Email</label>
              <input
                type="email"
                value={newApplicant.email}
                onChange={e => setNewApplicant(prev => ({ ...prev, email: e.target.value }))}
                style={ps.inputStyle}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                className="hover-btn-secondary"
                onClick={() => setShowAddModal(false)}
                style={ps.buttonSecondary}
              >
                Cancel
              </button>
              <button
                className="hover-btn-primary"
                onClick={handleAddApplicant}
                disabled={saving}
                style={{ ...ps.buttonPrimary, opacity: saving ? 0.7 : 1 }}
              >
                {saving ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportApplicantsModal
          onClose={() => setShowImportModal(false)}
          onImported={() => loadApplicants()}
        />
      )}
    </div>
  );
}
